/**
 * Booking API service - encapsulates all booking-related API calls
 * Dependency: apiClient, endpoints
 */

import { apiClient } from "../client";
import { endpoints } from "../endpoints";
import type {
  Field,
  Slot,
  Pricing,
  CreateReservationPayload,
  Reservation,
  PaymentResponse,
  PaymentDetailsResponse,
} from "@/lib/types/booking";

export const bookingService = {
  async getFields(date: string): Promise<Field[]> {
    const data = await apiClient.get<any[]>(endpoints.fields(date));
    if (!Array.isArray(data)) return [];
    return data.map((f: any) => ({
      fieldId: f.fieldId ?? f.id ?? 0,
      fieldName: f.fieldName ?? f.name ?? "",
      available: f.available ?? f.isAvailable ?? true,
    }));
  },

  async getSlots(date: string, fieldId: string | number): Promise<Slot[]> {
    const data = await apiClient.get<any[]>(endpoints.slots(date, fieldId));
    if (!Array.isArray(data)) return [];
    return data.map((item: any) => ({
      from: item.from ?? item.startTime ?? "",
      to: item.to ?? item.endTime ?? "",
      available: item.available ?? item.isAvailable ?? false,
      price: item.price ?? item.monto,
    }));
  },

  async getPricing(
    fieldId: string | number,
    from: string,
    to: string,
  ): Promise<Pricing | null> {
    try {
      const data = await apiClient.get<any>(endpoints.pricing(fieldId, from, to));
      if (!data) return null;
      return {
        totalAmount: data.totalAmount ?? 0,
        advanceAmount: data.advanceAmount ?? 0,
        remainingAmount: data.remainingAmount ?? 0,
        hours: data.hours ?? data.totalHours ?? 0,
        pricePerHour: data.pricePerHour ?? data.avgPricePerHour ?? 0,
      };
    } catch {
      return null;
    }
  },

  async createReservation(
    data: CreateReservationPayload,
  ): Promise<Reservation> {
    return apiClient.post<Reservation>(endpoints.reservations(), data);
  },

  async createPayment(
    reservationId: number,
    accessToken: string,
  ): Promise<PaymentResponse> {
    const data = await apiClient.post<any>(endpoints.paymentsCreate(), {
      reservationId,
      accessToken,
    });
    const initPoint =
      data?.initPoint ??
      data?.paymentUrl ??
      data?.init_point ??
      data?.sandbox_init_point ??
      data?.url;
    return {
      ...data,
      initPoint,
    };
  },

  async createAdvancePayment(
    reservationId: number,
    accessToken: string,
  ): Promise<PaymentResponse> {
    const data = await apiClient.post<any>(endpoints.paymentsCreateAdvance(), {
      reservationId,
      accessToken,
    });
    const initPoint =
      data?.initPoint ??
      data?.paymentUrl ??
      data?.init_point ??
      data?.sandbox_init_point ??
      data?.url;
    return {
      ...data,
      initPoint,
    };
  },

  async getPaymentDetails(
    reservationId: number,
    accessToken: string,
  ): Promise<PaymentDetailsResponse> {
    return apiClient.get<PaymentDetailsResponse>(
      endpoints.paymentsDetails(reservationId, accessToken),
    );
  },

  async triggerPaymentConfirmation(
    reservationId: number,
    paymentId: string,
  ): Promise<void> {
    await apiClient.post(endpoints.paymentsTriggerConfirmation(), {
      reservationId,
      paymentId,
    });
  },
};
